import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ModalForm } from '@/components/common/Modal'
import { TextInput, SelectInput, CheckboxInput } from '@/components/common/FormField'
import api from '@/services/api'
import { departmentService } from '@/services/api'
import toast from 'react-hot-toast'

const INITIAL = { name: '', short_name: '', department: '', is_active: true }

export default function DirectionModal({ open, onClose, direction = null, defaultDepartment = null }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(INITIAL)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const isEdit = !!direction

  const { data: departments } = useQuery({
    queryKey: ['departments-full'],
    queryFn: () => departmentService.list({ page_size: 200 }).then((r) => r.data.results || r.data),
  })

  useEffect(() => {
    if (open) {
      setForm(direction ? {
        name: direction.name || '',
        short_name: direction.short_name || '',
        department: direction.department || '',
        is_active: direction.is_active ?? true,
      } : {
        ...INITIAL,
        department: defaultDepartment || '',
      })
      setErrors({})
    }
  }, [open, direction, defaultDepartment])

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: val }))
    if (errors[key]) setErrors((er) => ({ ...er, [key]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Обязательное поле'
    if (!form.department) e.department = 'Выберите кафедру'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      if (isEdit) {
        await api.patch(`/departments/directions/${direction.id}/`, form)
        toast.success('Направление обновлено')
      } else {
        await api.post('/departments/directions/', form)
        toast.success('Направление создано')
      }
      qc.invalidateQueries(['departments-hierarchy'])
      qc.invalidateQueries(['directions-full'])
      onClose()
    } catch (err) {
      const data = err.response?.data
      if (data && typeof data === 'object') {
        const fieldErrors = {}
        Object.entries(data).forEach(([k, v]) => {
          fieldErrors[k] = Array.isArray(v) ? v[0] : v
        })
        setErrors(fieldErrors)
        const nonField = data.detail || data.non_field_errors?.[0]
        if (nonField) toast.error(nonField)
      } else {
        toast.error('Ошибка сохранения')
      }
    } finally {
      setLoading(false)
    }
  }

  const deptOptions = (departments || []).map((d) => ({ value: d.id, label: d.name }))

  return (
    <ModalForm
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редактировать направление' : 'Добавить направление'}
      onSubmit={handleSubmit}
      loading={loading}
    >
      <SelectInput
        label="Кафедра" required
        value={form.department}
        onChange={set('department')}
        error={errors.department}
        options={deptOptions}
        placeholder="Выберите кафедру"
      />
      <TextInput
        label="Название направления" required
        value={form.name}
        onChange={set('name')}
        error={errors.name}
        placeholder="Прикладная информатика"
      />
      <TextInput
        label="Короткое название"
        value={form.short_name}
        onChange={set('short_name')}
        placeholder="ПИ"
      />
      <CheckboxInput
        label="Активное направление"
        checked={form.is_active}
        onChange={set('is_active')}
      />
    </ModalForm>
  )
}
